import { z } from "zod";

/**
 * SEO validation schema for page content.
 * Blocks saving if critical SEO fields are invalid.
 */
export const seoSchema = z.object({
  title: z
    .string()
    .min(1, "SEO title is required")
    .max(60, "SEO title must be 60 characters or fewer"),
  description: z
    .string()
    .min(1, "Meta description is required")
    .min(140, "Meta description should be at least 140 characters")
    .max(160, "Meta description must be 160 characters or fewer"),
  canonicalUrl: z.string().optional(),
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
  ogImage: z.string().optional(),
  indexable: z.boolean().default(true),
  schemaType: z.string().optional(),
});

export type SEOData = z.infer<typeof seoSchema>;

/**
 * Page content validation schema.
 * Validates required fields before allowing save.
 */
export const pageContentSchema = z.object({
  page_title: z.string().min(1, "Page title is required").max(200),
  slug: z
    .string()
    .min(1, "Slug is required")
    .max(200)
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be lowercase alphanumeric with hyphens only"
    ),
  page_type: z.enum(["homepage", "service", "subservice", "standalone", "solution"]),
  status: z.enum(["draft", "review", "published", "archived"]),
});

/**
 * Protected slugs that must not be set to noindex.
 */
export const PROTECTED_NOINDEX_SLUGS = [
  "homepage",
  "services",
  "contact",
  "about",
  "solutions",
  "analytics",
  "paid-marketing",
  "seo",
  "cro",
  "email-marketing",
];

/**
 * Validate that noindex isn't being applied to protected pages.
 */
export const validateNoindex = (slug: string, indexable: boolean): string | null => {
  if (!indexable && PROTECTED_NOINDEX_SLUGS.includes(slug)) {
    return `Cannot set noindex on protected page "${slug}". Admin override required.`;
  }
  return null;
};

/**
 * Validate content has required structural elements.
 */
export const validateContentStructure = (content: Record<string, any>): string[] => {
  const errors: string[] = [];

  // Check for headline/h1 presence in content
  const contentStr = JSON.stringify(content);
  if (!contentStr.includes("headline") && !contentStr.includes("h1") && !contentStr.includes("title")) {
    errors.push("Content must include a headline (H1)");
  }

  return errors;
};
